import { useQuery } from "@tanstack/react-query"
import { LoaderCircle, Moon, Plus } from "lucide-react"
import { useState } from "react"
import { dashboardQueryOptions } from "@/features/dashboard/api/dashboard.queries"
import { SessionFormDialog } from "@/features/sleep/SessionFormDialog"
import { Button } from "@/shared/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shared/components/ui/sheet"
import { cn } from "@/shared/lib/utils"
import { SleepControls } from "./SleepControls"

// Non-suspense query so the always-rendered nav never suspends on routes that
// don't prefetch it.
export function SleepDrawer({ childId }: { childId: string }) {
  const [open, setOpen] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const { data: dashboard } = useQuery(dashboardQueryOptions(childId))
  const sleeping = dashboard?.currentSession != null

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger
          aria-label={sleeping ? "Avsluta sömn" : "Registrera sömn"}
          className={cn(
            "mx-1 flex size-14 shrink-0 items-center justify-center rounded-full text-white",
            "bg-gradient-to-br from-primary to-accent shadow-lg shadow-primary/40",
            sleeping && "animate-sleep-pulse",
          )}
        >
          {sleeping ? <Moon className="size-6 animate-pulse" /> : <Plus className="size-6" />}
        </SheetTrigger>
        <SheetContent side="bottom" className="mx-auto max-w-[30rem] rounded-t-2xl">
          <SheetHeader>
            <SheetTitle>{sleeping ? "Avsluta sömnpasset" : "Registrera sömn"}</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-4 px-4 pb-6">
            {dashboard ? (
              <SleepControls dashboard={dashboard} onDone={() => setOpen(false)} />
            ) : (
              <div className="flex justify-center py-6">
                <LoaderCircle className="size-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {/* Close the drawer as this opens, so only the dialog is left on screen. */}
            <Button
              variant="link"
              size="sm"
              className="mx-auto h-auto text-muted-foreground hover:text-foreground"
              onClick={() => {
                setOpen(false)
                setFormOpen(true)
              }}
            >
              Lägg till i efterhand
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Rendered outside the Sheet so it survives the drawer closing. */}
      <SessionFormDialog childId={childId} open={formOpen} onOpenChange={setFormOpen} />
    </>
  )
}
